import React, { useState, useEffect } from 'react';
import AmazonProductCard from '../components/AmazonDeals/AmazonProductCard';
import API from '../api/apiConfig';

const AmazonDealsPage = () => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchProducts = async (currentPage) => {
    setLoading(true);
    try {
      console.log(`Fetching more drops (Page ${currentPage})`);
      const response = await API.post(`/api/get-recent-drops`, {
        page: currentPage - 1, // API is 0-indexed
        limit: 32,
        sid_arr: [2], // Amazon.in
      });

      console.log('API Response:', response.data);
      if (response.data.data && response.data.data.length > 0) {
        console.log('Sample Raw Product:', response.data.data[0]);
      }

      const newProducts = (response.data.data || []).map(p => ({
        ...p,
        sid: 2, // Always Amazon as requested
        pid: p.pid || p.p_pid || p.p_id || p.productId || p.PID,
        title: p.title || p.p_name || p.name,
        price: p.price || p.p_price,
        mrp: p.mrp || p.p_mrp,
        imgurl: p.imgurl || p.p_img || p.img,
        pt1id: p.pt1id || p.fspid,
        brand: p.brand || p.p_brand || ""
      }));

      if (newProducts.length === 0) {
        setHasMore(false);
      } else {
        // Map data safely and check if we have more
        setProducts((prev) => [...prev, ...newProducts]);
        if (newProducts.length < 32) setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching Amazon recent drops:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(1);
  }, []);

  const handleViewMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage);
  };

  return (
    <div className="bg-[#FAFAFB] min-h-screen pb-4 sm:px-4 font-inter">
      {/* Dynamic Hero Section - Flushed with Navbar */}
      <section className="relative bg-white pt-4 pb-6 sm:pb-8 px-4 overflow-hidden border-b border-gray-100">
        <div className="absolute top-0 right-0 w-[40%] h-[120%] bg-[#7E54F2]/5 rounded-bl-[200px] -z-0"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#7E54F2]/10 rounded-full blur-3xl -z-0"></div>

        <div className="max-w-[1444px] mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#7E54F2]/10 text-[#7E54F2] px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-[#7E54F2]/20 shadow-sm animate-bounce-subtle">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7E54F2] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7E54F2]"></span>
            </span>
            Live Amazon Deals
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 md:mb-6 leading-[1.1] tracking-tighter text-gray-900">
            Unbeatable <span className="text-[#7E54F2] italic">Loot</span> <br />
            Deals Handpicked.
          </h1>

          <p className="text-gray-500 text-sm sm:text-base md:text-lg max-w-2xl mx-auto leading-relaxed font-medium mb-6 md:mb-8">
            Discover the best deals across thousands of Amazon products.
            Grab them before they’re gone.
          </p>

          <div className="flex items-center justify-center gap-4">
            <div className="h-[2px] w-12 bg-[#7E54F2]/20 rounded-full"></div>
            <span className="text-xs font-black text-gray-300 uppercase tracking-[0.4em]">Explore Recent Drops</span>
            <div className="h-[2px] w-12 bg-[#7E54F2]/20 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Main Grid Content - Increased density */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 mt-8 md:mt-12">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2 sm:gap-6">
          {products.map((product, idx) => (
            <AmazonProductCard key={`${product.pid}-${idx}`} product={product} />
          ))}
        </div>

        {/* Loading Skeleton / Empty State */}
        {products.length === 0 && !loading && (
          <div className="text-center py-32 flex flex-col items-center">
            <div className="w-24 h-24 bg-white rounded-[40px] shadow-2xl flex items-center justify-center mb-8 border border-gray-100 animate-float">
              <span className="text-4xl">🔎</span>
            </div>
            <p className="text-gray-400 font-black text-xl uppercase tracking-tighter">No active drops found.</p>
            <p className="text-gray-300 text-sm mt-2">Check back in a few minutes for fresh deals.</p>
          </div>
        )}

        {/* Modern View More Button */}
        {hasMore && (
          <div className="mt-24 flex justify-center">
            <button
              onClick={handleViewMore}
              disabled={loading}
              className="group relative px-12 py-5 bg-white border border-gray-100 rounded-[30px] shadow-[0_10px_40px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_60px_rgba(126,84,242,0.15)] transition-all duration-500 active:scale-95 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#7E54F2]/0 via-[#7E54F2]/5 to-[#7E54F2]/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              <div className="flex flex-col items-center relative z-10">
                <span className={`text-lg font-black tracking-tight ${loading ? 'text-gray-300' : 'text-gray-900 group-hover:text-[#7E54F2] transition-colors'}`}>
                  {loading ? 'Analyzing Fresh Drops...' : 'Show More Deals'}
                </span>
                <div className={`h-[2px] bg-[#7E54F2] rounded-full transition-all duration-500 ${loading ? 'w-12 animate-pulse' : 'w-6 group-hover:w-16 mt-2'}`}></div>
              </div>
            </button>
          </div>
        )}

        {!hasMore && products.length > 0 && (
          <div className="mt-20 text-center py-10 border-t border-gray-100 italic font-medium text-gray-400">
            You've reached the end of the top drops. Check back soon for more!
          </div>
        )}
      </main>
    </div>
  );
};

export default AmazonDealsPage;
